/* eslint-disable @typescript-eslint/no-unused-vars */
import React,{useState,useEffect} from 'react';
import Layout from '../components/layout/Layout';
import api from '../services/api';

export default function TokenQueuePage(){
  const [appointments,setAppointments]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const [date,setDate]=useState(new Date().toISOString().split('T')[0]);
  const [doctorFilter,setDoctorFilter]=useState('');
  const [doctors,setDoctors]=useState<any[]>([]);
  const [displayMode,setDisplayMode]=useState<'board'|'list'>('board');

  const load=async()=>{
    setLoading(true);
    try{
      const r=await api.getAppointments({date,status:'SCHEDULED,CHECKED_IN,IN_PROGRESS,COMPLETED',take:200});
      const list=Array.isArray(r?.data?.appointments)?r.data.appointments:Array.isArray(r?.data)?r.data:[];
      setAppointments(list);
    }catch{}
    setLoading(false);
  };

  useEffect(()=>{
    load();
    api.getDoctors().then((r:any)=>setDoctors(Array.isArray(r.data)?r.data:[])).catch(()=>{});
    const interval=setInterval(load,30000); // auto-refresh every 30 sec
    return ()=>clearInterval(interval);
  },[date,doctorFilter]);

  const filtered=doctorFilter?appointments.filter(a=>(a.doctorId||a.doctor_id)===doctorFilter):appointments;

  const STATUS_CONFIG:Record<string,{label:string;color:string;bg:string;order:number}>={
    CHECKED_IN:   {label:'Waiting',       color:'text-amber-700',  bg:'bg-amber-50 border-amber-300',   order:1},
    IN_PROGRESS:  {label:'With Doctor',   color:'text-green-700',  bg:'bg-green-50 border-green-400',   order:0},
    SCHEDULED:    {label:'Scheduled',     color:'text-[#02357A]',   bg:'bg-blue-50 border-blue-200',     order:2},
    COMPLETED:    {label:'Done',          color:'text-slate-500',  bg:'bg-slate-50 border-slate-200',   order:3},
    CANCELLED:    {label:'Cancelled',     color:'text-red-500',    bg:'bg-red-50 border-red-200',       order:4},
  };

  const sorted=[...filtered].sort((a,b)=>{
    const oa=STATUS_CONFIG[a.status]?.order??5;
    const ob=STATUS_CONFIG[b.status]?.order??5;
    if(oa!==ob) return oa-ob;
    return (a.token_number||a.tokenNumber||0)-(b.token_number||b.tokenNumber||0);
  });

  const now=sorted.find(a=>a.status==='IN_PROGRESS');
  const waiting=sorted.filter(a=>a.status==='CHECKED_IN');
  const upcoming=sorted.filter(a=>a.status==='SCHEDULED').slice(0,5);

  return(
    <Layout title="Token Queue & Waiting Room" actions={
      <div className="flex gap-2 items-center">
        <input type="date" className="input text-sm" value={date} onChange={e=>setDate(e.target.value)}/>
        <select className="input text-sm w-48" value={doctorFilter} onChange={e=>setDoctorFilter(e.target.value)}>
          <option value="">All Doctors</option>
          {doctors.map((d:any)=><option key={d.id} value={d.id}>Dr. {d.firstName||d.first_name} {d.lastName||d.last_name||''}</option>)}
        </select>
        <button onClick={()=>setDisplayMode(m=>m==='board'?'list':'board')} className="btn-secondary text-sm">
          {displayMode==='board'?'📋 List':'📺 Board'}
        </button>
        <button onClick={load} className="btn-secondary text-sm">🔄</button>
      </div>
    }>
      {/* BOARD MODE — for TV display */}
      {displayMode==='board'&&(
        <div className="space-y-4">
          {/* Now serving */}
          <div className={`rounded-2xl border-2 p-6 ${now?'border-green-400 bg-green-50':'border-slate-200 bg-slate-50'}`}>
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">🟢 Now Serving</p>
            {now?(
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-6xl font-black text-green-700">{now.token_number||now.tokenNumber||'—'}</p>
                  <p className="text-xl font-semibold text-slate-700 mt-1">{now.patient_first_name||''} {now.patient_last_name||''}</p>
                  <p className="text-sm text-slate-400">Dr. {now.doctor_first_name||''} {now.doctor_last_name||''} · {now.specialization||''}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400">Room / Chamber</p>
                  <p className="text-4xl font-bold text-green-600">—</p>
                </div>
              </div>
            ):<p className="text-2xl text-slate-300 font-semibold">No patient currently with doctor</p>}
          </div>

          {/* Waiting */}
          <div>
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">⏳ Waiting ({waiting.length})</p>
            {waiting.length===0?<p className="text-slate-300 text-sm">No patients waiting</p>:(
              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                {waiting.map((a:any)=>(
                  <div key={a.id} className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3 text-center">
                    <p className="text-3xl font-black text-amber-700">{a.token_number||a.tokenNumber}</p>
                    <p className="text-xs text-slate-500 truncate mt-1">{a.patient_first_name||''}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Next up */}
          {upcoming.length>0&&(
            <div>
              <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">📅 Upcoming</p>
              <div className="flex gap-2 flex-wrap">
                {upcoming.map((a:any)=>(
                  <div key={a.id} className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 text-sm">
                    <span className="font-bold text-[#02357A]">#{a.token_number||a.tokenNumber}</span>
                    <span className="text-slate-500 ml-2">{a.patient_first_name||''} {a.patient_last_name||''}</span>
                    <span className="text-slate-400 ml-2">{(a.start_time||a.startTime||'').slice(0,5)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* LIST MODE — for front desk */}
      {displayMode==='list'&&(
        <div className="card overflow-hidden">
          <div className="grid grid-cols-10 p-3 bg-slate-50 border-b text-xs font-semibold text-slate-500 uppercase gap-2">
            <span>Token</span><span className="col-span-2">Patient</span><span className="col-span-2">Doctor</span><span>Time</span><span>Type</span><span className="col-span-2">Status</span><span>Action</span>
          </div>
          {loading?<div className="p-8 text-center text-slate-400">Loading...</div>
          :sorted.length===0?<div className="p-8 text-center text-slate-400">No appointments for {date}</div>
          :<div className="divide-y max-h-[65vh] overflow-y-auto">
            {sorted.map((a:any)=>{
              const cfg=STATUS_CONFIG[a.status]||{label:a.status,color:'text-slate-600',bg:'bg-slate-50 border-slate-200',order:5};
              return(
                <div key={a.id} className={`grid grid-cols-10 gap-2 p-3 items-center text-sm border-l-4 ${a.status==='IN_PROGRESS'?'border-l-green-400 bg-green-50':a.status==='CHECKED_IN'?'border-l-amber-400':'border-l-transparent'}`}>
                  <span className={`font-black text-xl ${a.status==='IN_PROGRESS'?'text-green-700':a.status==='CHECKED_IN'?'text-amber-700':'text-slate-400'}`}>{a.token_number||a.tokenNumber||'—'}</span>
                  <div className="col-span-2">
                    <p className="font-medium">{a.patient_first_name||''} {a.patient_last_name||''}</p>
                    <p className="text-xs font-mono text-slate-400">{a.uhid||''}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm">Dr. {a.doctor_first_name||''} {a.doctor_last_name||''}</p>
                    {a.specialization&&<p className="text-xs text-slate-400">{a.specialization}</p>}
                  </div>
                  <span className="text-xs text-slate-500">{(a.start_time||a.startTime||'').slice(0,5)}</span>
                  <span className="text-xs text-slate-400">{a.consultation_type||a.consultationType||'OPD'}</span>
                  <div className="col-span-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                  </div>
                  <div className="flex gap-1">
                    {a.status==='SCHEDULED'&&<button onClick={()=>api.updateAppointmentStatus?.(a.id,'CHECKED_IN').then(load)} className="text-xs bg-amber-100 text-amber-700 hover:bg-amber-200 px-2 py-0.5 rounded font-semibold transition-all">Check In</button>}
                    {a.status==='CHECKED_IN'&&<span className="text-xs text-amber-600 font-semibold">Waiting</span>}
                    {a.status==='IN_PROGRESS'&&<span className="text-xs text-green-600 font-semibold">● Live</span>}
                  </div>
                </div>
              );
            })}
          </div>}
        </div>
      )}
    </Layout>
  );
}
